import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Container, CardMedia, Typography, Box, Button, CircularProgress, Card, CardContent, CardActions, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Divider } from '@mui/material';
import { primaryColor } from '../theme';
import '../App.css';
import moment from 'moment';

const ReviewContentPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/content/${id}/`);
        setContent(response.data);
        setComments(response.data.Comments || []);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching content:', error);
        setLoading(false);
      }
    };

    fetchContent();
  }, [id]);

  const userId = JSON.parse(localStorage.getItem('user')).id;

  const handleApprove = async () => {
    try {
      await axios.put(`${process.env.REACT_APP_API_BASE_URL}/content/${id}/review`, { status: 'Approved', userId, approvalDate: new Date().toISOString() });
      navigate('/approver/dashboard', { state: { message: 'Content approved successfully!' } });
    } catch (error) {
      console.error('Error approving content:', error);
      navigate('/approver/dashboard', { state: { message: 'Error approving content.' } });
    }
  };

  const handleRequestChanges = async () => {
    try {
      await axios.put(`${process.env.REACT_APP_API_BASE_URL}/content/${id}/review`, { status: 'Review', comment, userId });
      setDialogOpen(false);
      handleAddComment();
      navigate('/approver/dashboard', { state: { message: 'Change request submitted successfully!' } });
    } catch (error) {
      console.error('Error requesting changes:', error);
      navigate('/approver/dashboard', { state: { message: 'Error requesting changes.' } });
    }
  };

  const handleAddComment = async () => {
    const user = JSON.parse(localStorage.getItem('user'));

    if (!user) {
      console.error('User not found in localStorage');
      return;
    }
    
    try {
      const newComment = { body: comment, date: new Date().toISOString(), userId: user.id };
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/content/${id}/comments`, newComment);
      setComments([...comments, newComment]);
      setComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!content) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography variant="h6" color="error">Content not found</Typography>
      </Container>
    );
  }

  const getPresentationEmbedUrl = (url) => {
    if (url.endsWith('.pptx')) {
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
    }
    return url;
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Button onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        Back to Dashboard
      </Button>
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>{content.topic}</Typography>
          <Typography variant="subtitle1" gutterBottom>Target User: {content.targetUser}</Typography>
          <Typography variant="subtitle1" gutterBottom>Value Chain: {content.valueChain}</Typography>
          <Typography variant="subtitle1" gutterBottom>Platform: {content.platform}</Typography>
          {content.video && (
            <Box mt={2}>
              <CardMedia
                component="video"
                controls
                height="200"
                src={content.video}
                title={content.topic}
              />
            </Box>
          )}
          {content.audio && (
            <Box mt={2}>
              <audio controls>
                <source src={content.audio} type="audio/mpeg" />
                Your browser does not support the audio element.
              </audio>
            </Box>
          )}
          {content.presentation && (
            <Box mt={2}>
              <iframe
                src={getPresentationEmbedUrl(content.presentation)}
                width="100%"
                height="400"
                title={content.topic}
                frameBorder="0"
                allowFullScreen
              />
            </Box>
          )}
          <Typography variant="body1" dangerouslySetInnerHTML={{ __html: content.content }} />
          <Typography variant="subtitle2" gutterBottom>Date Uploaded: {moment(content.dateUploaded).format('YYYY-MM-DD')}</Typography>
          <Typography variant="subtitle2" gutterBottom>Views: {content.views}</Typography>
          <Typography variant="subtitle2" gutterBottom>Status: {content.status}</Typography>
          <Typography variant="subtitle2" gutterBottom>Approval Date: {content.approvalDate ? moment(content.approvalDate).format('YYYY-MM-DD') : 'Not Approved'}</Typography>
        </CardContent>
        <CardActions>
          <Button variant="contained" color="primary" onClick={handleApprove} disabled={content.status === 'Approved'}>
            {content.status === 'Approved' ? 'Approved' : 'Approve'}
          </Button>
          <Button variant="contained" color="secondary" onClick={handleDialogOpen}>
            Request Changes
          </Button>
        </CardActions>
      </Card>
      <Divider sx={{ my: 4 }} />
      <Box mt={4} mb={4}>
        <Typography variant="h6" gutterBottom>Comments</Typography>
        {comments.map((comment, index) => (
          <Box key={index} mb={2}>
            <Typography variant="body2">{comment.body}</Typography>
            <Typography variant="caption" color="textSecondary">
              {comment.User.firstName} ({comment.User.role}) - {moment(comment.date).format('YYYY-MM-DD HH:mm')}
            </Typography>
          </Box>
        ))}
        <TextField
          label="Add a comment"
          multiline
          rows={4}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          fullWidth
          variant="outlined"
          sx={{ mt: 2 }}
        />
        <Button variant="contained" color="primary" onClick={handleAddComment} sx={{ mt: 2 }}>
          Add Comment
        </Button>
      </Box>
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Request Changes</DialogTitle>
        <DialogContent>
          <TextField
            label="Comment"
            multiline
            rows={4}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleRequestChanges} color="secondary">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ReviewContentPage;